"use client";

import React, { useState, useRef, useEffect } from 'react';

const TEXT_API = "http://127.0.0.1:1880/agent_FrontDChat";
const VOICE_API = "http://127.0.0.1:1880/agent_FrontDVoice";

let addMessage;

const ChatBox = ({ isOpen, onClose, onExternalMessage }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const messagesEndRef = useRef(null);
  const isAtBottom = useRef(true);
  const audioRef = useRef(null);

  const playBase64Audio = async (base64String) => {
    try {
      console.log('Received base64 audio data:', base64String.substring(0, 50) + '...');
      
      // Remove any potential header from the base64 string
      const cleanBase64 = base64String.replace(/^data:audio\/(mp3|mpeg);base64,/, '');
      console.log('Cleaned base64 string:', cleanBase64.substring(0, 50) + '...');

      // Convert base64 to blob
      const byteCharacters = atob(cleanBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'audio/mp3' });
      console.log('Created audio blob:', blob);

      // Create audio URL
      const audioUrl = URL.createObjectURL(blob);
      console.log('Created audio URL:', audioUrl);

      // Create a new audio element
      const audio = new Audio(audioUrl);
      audio.onloadedmetadata = () => {
        console.log('Audio duration:', audio.duration);
      };
      audio.onerror = (e) => {
        console.error('Audio error:', e);
      };

      // Play the audio
      try {
        const playPromise = await audio.play();
        console.log('Audio playback started');
        return audioUrl;
      } catch (playError) {
        console.error('Playback error:', playError);
        return null;
      }
    } catch (error) {
      console.error('Error in playBase64Audio:', error);
      return null;
    }
  };

  addMessage = (text, isUser, audioUrl = undefined) => {
    const newMessage = { text, isUser, timestamp: new Date(), audioUrl };
    setMessages(prev => [...prev, newMessage]);
    if (isUser && !audioUrl) {
      sendTextMessageToAPI(text);
    }
  };

  const scrollToBottom = () => {
    if (messagesEndRef.current && isAtBottom.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleScroll = () => {
    const chatBox = messagesEndRef.current?.parentElement?.parentElement;
    if (chatBox) {
      const isBottom = chatBox.scrollHeight - chatBox.scrollTop <= chatBox.clientHeight + 5;
      isAtBottom.current = isBottom;
    }
  };

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addMessage("Welcome! I am BALL-E, an AI assistant.", false);
      addMessage("I can help you on any info regarding our projects or connect you with our team.", false);
      addMessage("How may I assist you today?", false);
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (onExternalMessage) {
      onExternalMessage((message) => {
        addMessage(message, true);
      });
    }
  }, [onExternalMessage]);

  const handleAPIResponse = async (data) => {
    console.log('Received API response:', data);
    
    // Check if this is an audio response
    if (data.type === 'audio' || data.format === 'mp3') {
      console.log('Processing audio response');
      
      // If we have a base64 audio in response
      if (data.response && typeof data.response === 'string' && 
          (data.response.startsWith('/') || data.response.startsWith('data:audio'))) {
        
        // Play the audio from response field
        const audioUrl = await playBase64Audio(data.response);
        
        // Add message with transcript
        if (data.agent_transcript) {
          addMessage(data.agent_transcript, false, audioUrl || undefined);
        } else {
          addMessage("Audio message", false, audioUrl || undefined);
        }
      } 
      // If we have audio in contentBytes field
      else if (data.contentBytes) {
        const audioUrl = await playBase64Audio(data.contentBytes);
        
        if (data.agent_transcript) {
          addMessage(data.agent_transcript, false, audioUrl || undefined);
        } else if (data.response && typeof data.response === 'string' && 
                  !data.response.startsWith('/') && !data.response.startsWith('data:audio')) {
          addMessage(data.response, false, audioUrl || undefined);
        } else {
          addMessage("Audio message", false, audioUrl || undefined);
        }
      }
      // If no audio data found
      else if (data.response && typeof data.response === 'string') {
        addMessage(data.response, false);
      }
    } 
    // Text response
    else if (data.response && typeof data.response === 'string') {
      console.log('Processing text response');
      addMessage(data.response, false);
    }

    // Update user transcript if available
    if (data.user_transcript) {
      setMessages(prev => {
        const newMessages = [...prev];
        for (let i = newMessages.length - 1; i >= 0; i--) {
          if (newMessages[i].isUser && newMessages[i].text === "Voice message") {
            newMessages[i].text = data.user_transcript || "Voice message";
            break;
          }
        }
        return newMessages;
      });
    }

    if (data.threadid && !threadId) {
      setThreadId(data.threadid);
    }
  };

  const sendTextMessageToAPI = async (text) => {
    try {
      let response, data;
      if (text.length < 100) {
        const url = threadId
          ? `${TEXT_API}?query=${encodeURIComponent(text)}&threadid=${threadId}`
          : `${TEXT_API}?query=${encodeURIComponent(text)}`;
        response = await fetch(url);
        data = await response.json();
      } else {
        response = await fetch(TEXT_API, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ query: text, threadid: threadId || undefined })
        });
        data = await response.json();
      }

      await handleAPIResponse(data);
    } catch (error) {
      console.error("Text API error:", error);
      addMessage("Sorry.The bot is currently offline", false);
    }
  };

  const sendTextMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim()) return;

    addMessage(inputText, true);
    setInputText('');
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      alert("Your browser does not support audio recording.");
      return;
    }

    setIsRecording(true);
    audioChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = event => {
        if (event.data.size > 0) audioChunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = sendVoiceMessage;
      mediaRecorder.start();
    } catch (error) {
      console.error("Error accessing microphone:", error);
      setIsRecording(false);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    }
    setIsRecording(false);
  };

  const sendVoiceMessage = async () => {
    if (audioChunksRef.current.length === 0) return;

    const audioBlob = new Blob(audioChunksRef.current, { type: "audio/wav" });
    const audioUrl = URL.createObjectURL(audioBlob);
    const formData = new FormData();
    formData.append("file", audioBlob, "recording.wav");

    addMessage("Voice message", true, audioUrl);

    try {
      const url = threadId ? `${VOICE_API}?threadid=${threadId}` : VOICE_API;
      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });
      const data = await response.json();
      await handleAPIResponse(data);
    } catch (error) {
      console.error("Voice API error:", error);
      addMessage("Sorry.The bot is currently offline", false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-24 right-6 w-96 max-w-[95vw] h-[600px] max-h-[80vh] bg-gray-900 rounded-lg shadow-2xl z-[100] flex flex-col">
      <div className="bg-[#e01f26] p-4 rounded-t-lg flex justify-between items-center border-b border-gray-600">
        <h3 className="text-gray-200 font-semibold">BALL-E 🤖 [Offline]
     </h3>
        <button
          onClick={onClose}
          className="text-gray-200 hover:text-gray-100 transition-colors"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
      <audio ref={audioRef} className="hidden" />
      <div className="flex-1 p-4 overflow-y-auto bg-gray-800" onScroll={handleScroll}>
        <div className="space-y-4">
          {messages.map((msg, index) => (
            <div key={index} className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`rounded-lg p-3 max-w-[80%] ${
                  msg.isUser
                    ? 'bg-gradient-to-r from-blue-900 to-gray-700 text-gray-200'
                    : 'bg-gray-700 text-gray-200'
                }`}
              >
                <p className="text-sm sm:text-base">{msg.text}</p>
                {msg.audioUrl && (
                  <audio controls className="max-w-full mt-2">
                    <source src={msg.audioUrl} type="audio/mp3" />
                    Your browser does not support the audio element.
                  </audio>
                )}
                <span className="text-xs text-gray-400 mt-1 block">
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
      <form onSubmit={sendTextMessage} className="p-3 sm:p-4 border-t border-gray-700 bg-gray-900">
        <div className="flex gap-2">
          <input
            type="text"
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 p-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#e01f26] bg-gray-800 text-gray-200 text-sm sm:text-base"
          />
          <button
            type="submit"
            className="bg-[#e01f26] text-gray-200 px-2 sm:px-4 py-2 rounded-lg hover:bg-[#c01920] transition-colors text-sm sm:text-base"
          >
            Send
          </button>
          <button
            type="button"
            className={`px-2 sm:px-4 py-2 rounded-lg text-gray-200 transition-colors ${
              isRecording 
                ? 'bg-[#c01920] hover:bg-[#a01518]' 
                : 'bg-[#e01f26] hover:bg-[#c01920]'
            }`}            
            onMouseDown={startRecording}
            onMouseUp={stopRecording}
            onTouchStart={startRecording}
            onTouchEnd={stopRecording}
          >
            🎙️
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatBox;
export { addMessage };